import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

const HTML_TAG_PATTERN = /<\/?[a-zA-Z][^>]*>/;
const URL_PATTERN = /\b(?:https?:\/\/|www\.|[a-z0-9-]+\.(?:com|net|org|edu|gov|co|io|ai|au|uk|in|info|biz)(?:\/|\b))/i;

function decodeHtmlEntities(value: string): string {
  return value
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/&apos;/gi, "'")
    .replace(/&amp;/gi, '&')
    .replace(/&#x([0-9a-f]+);/gi, (_match, hex: string) => String.fromCharCode(parseInt(hex, 16)))
    .replace(/&#(\d+);/g, (_match, dec: string) => String.fromCharCode(parseInt(dec, 10)));
}

function decodeUriSafely(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

function normalizePotentiallyEncodedValue(value: string): string {
  let normalized = value;

  for (let i = 0; i < 2; i += 1) {
    const nextValue = decodeHtmlEntities(decodeUriSafely(normalized));
    if (nextValue === normalized) {
      break;
    }
    normalized = nextValue;
  }

  return normalized;
}

export function containsHtmlTags(value: string): boolean {
  return HTML_TAG_PATTERN.test(normalizePotentiallyEncodedValue(value));
}

export function removeHtmlTags(value: string): string {
  return normalizePotentiallyEncodedValue(value).replace(new RegExp(HTML_TAG_PATTERN, 'g'), '');
}

export function containsUrl(value: string): boolean {
  return URL_PATTERN.test(value);
}

export function noHtmlTagsValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (typeof control.value !== 'string' || control.value.length === 0) {
      return null;
    }

    return containsHtmlTags(control.value) ? { htmlTags: true } : null;
  };
}

export function noUrlsValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (typeof control.value !== 'string' || control.value.length === 0) {
      return null;
    }

    return containsUrl(control.value) ? { urlNotAllowed: true } : null;
  };
}

export function trimRequiredValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (control.value === null || control.value === undefined) {
      return null;
    }

    if (typeof control.value === 'string' && control.value.trim().length === 0) {
      return { required: true };
    }

    return null;
  };
}

export function normalizeInputValue<T>(value: T): T {
  if (typeof value !== 'string') {
    return value;
  }

  return value.trim() as T;
}

export function sanitizePlainTextValue<T>(value: T, trim = false): T {
  if (typeof value !== 'string') {
    return value;
  }

  const sanitizedValue = removeHtmlTags(value);
  return (trim ? sanitizedValue.trim() : sanitizedValue) as T;
}
